import { useState, useEffect, useCallback } from 'react';
import { getFCMToken, savePushToken, onForegroundMessage } from '../../firebase';
import { apiClient } from '../../services/apiClient';
import toast from 'react-hot-toast';

const DISMISSED_KEY = 'acs_push_dismissed';
const NEVER_KEY = 'acs_push_never';
const LAST_PROMPT_KEY = 'acs_push_last_prompt';

export interface PushNotificationState {
  permission: NotificationPermission;
  isSupported: boolean;
  isSubscribed: boolean;
  isLoading: boolean;
  shouldPrompt: boolean;
}

export const usePushNotifications = (user: any) => {
  const [state, setState] = useState<PushNotificationState>({
    permission: 'default',
    isSupported: 'Notification' in window && 'serviceWorker' in navigator,
    isSubscribed: false,
    isLoading: true,
    shouldPrompt: false,
  });

  const checkSubscription = useCallback(async () => {
    if (!state.isSupported) {
      setState(s => ({ ...s, isLoading: false }));
      return;
    }

    try {
      const permission = Notification.permission;
      let isSubscribed = false;

      if (permission === 'granted' && user) {
        const token = await getFCMToken();
        isSubscribed = !!token;
      }

      const never = localStorage.getItem(NEVER_KEY) === 'true';
      const dismissed = localStorage.getItem(DISMISSED_KEY) === 'true';
      const lastPrompt = parseInt(localStorage.getItem(LAST_PROMPT_KEY) || '0', 10);
      const now = Date.now();
      const promptCooldown = 7 * 24 * 60 * 60 * 1000; // 1 week

      const shouldPrompt = 
        user && 
        permission === 'default' && 
        !never && 
        (!dismissed || (now - lastPrompt > promptCooldown));

      setState(s => ({
        ...s,
        permission,
        isSubscribed,
        isLoading: false,
        shouldPrompt
      }));
    } catch (err) {
      console.warn('[usePushNotifications] checkSubscription failed:', err);
      setState(s => ({ ...s, isLoading: false }));
    }
  }, [state.isSupported, user]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const subscribe = useCallback(async () => {
    if (!state.isSupported) return false;
    
    setState(s => ({ ...s, isLoading: true }));
    try {
      const permission = await Notification.requestPermission();
      setState(s => ({ ...s, permission }));

      if (permission === 'granted') {
        const token = await getFCMToken();
        if (token) {
          // Register with server
          await apiClient.post('/Notifications/fcm-token', { token });
          // Also save to Firestore (legacy support)
          await savePushToken(token);
          
          setState(s => ({ ...s, isSubscribed: true, shouldPrompt: false }));
          toast.success('Push notifications enabled!');
          return true;
        }
      } else if (permission === 'denied') {
        localStorage.setItem(NEVER_KEY, 'true');
      }
    } catch (err) {
      console.error('[usePushNotifications] Subscription failed:', err);
      toast.error('Failed to enable push notifications.');
    } finally {
      setState(s => ({ ...s, isLoading: false }));
    }
    return false;
  }, [state.isSupported]);

  const unsubscribe = useCallback(async () => {
    if (!state.isSupported || !user) return;
    
    setState(s => ({ ...s, isLoading: true }));
    try {
      const token = await getFCMToken();
      if (token) {
        await apiClient.post('/Notifications/fcm-token/unregister', { token });
      }
      setState(s => ({ ...s, isSubscribed: false }));
      toast.success('Push notifications disabled.');
    } catch (err) {
      console.error('[usePushNotifications] Unsubscription failed:', err);
    } finally {
      setState(s => ({ ...s, isLoading: false }));
    }
  }, [state.isSupported, user]);

  const dismissPrompt = useCallback((never = false) => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    localStorage.setItem(LAST_PROMPT_KEY, Date.now().toString());
    if (never) {
      localStorage.setItem(NEVER_KEY, 'true');
    }
    setState(s => ({ ...s, shouldPrompt: false }));
  }, []);

  return {
    ...state,
    subscribe,
    unsubscribe,
    dismissPrompt,
    refresh: checkSubscription
  };
};
